import isFunction from 'lodash/isFunction';
import { CaretDown } from '@phosphor-icons/react';
import { css, cx, etiColors, mediaQueries, resetButtonStyle } from '@eti/styles';
import React from 'react';
import { breakpoints, Inline, Stack, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { useMediaQuery } from 'usehooks-ts';
import ResetButton from './ResetButton';

const buttonStyles = css`
  width: 100%;

  &:focus-visible {
    color: ${vars.colors.links.focus};
    outline: 2px solid ${vars.colors.focusRing};
    outline-offset: 2px;
  }

  @media ${mediaQueries.medium.up} {
    flex-wrap: wrap;
    padding-top: 15px;
  }

  @media ${mediaQueries.medium.down} {
    padding-bottom: 15px;
  }
`;

const clickable = css`
  cursor: pointer;
`;

const headerStyles = css`
  color: ${etiColors.black};
  font-size: 1rem;
  font-weight: 700;
  line-height: 19px;
`;

const controlsStyles = css`
  color: ${etiColors.grayScale[10]};
`;

const iconStyles = (isOpen: boolean) => css`
  transform: rotate(${isOpen ? '180deg' : '0deg'});
`;

const separatorStyles = css`
  display: inline-block;
  padding: 0 3px;
`;

const inlineStyles = css`
  justify-content: space-between;
`;

interface FilterHeaderProps {
  destinationName: string;
  isOpen: boolean;
  isToggleable: boolean;
  label: string;
  name: string;
  onClick: () => void;
  onReset?: () => void;
  originName: string;
}

const FilterHeader = ({
  destinationName,
  isOpen,
  isToggleable = true,
  label,
  name,
  onClick,
  onReset,
  originName,
}: FilterHeaderProps) => {
  const { t } = useTranslation();
  const isMobileScreen = useMediaQuery(`(max-width: ${breakpoints._768})`);

  const handleReset = (e: React.MouseEvent) => {
    if (isFunction(onReset)) {
      onReset();
      e.stopPropagation();
    }
  };

  const ariaLabel = label || t('ScreenReader.From.To.Text', originName, destinationName);
  const getAriaExpandedValue = () => {
    if (isMobileScreen) {
      return undefined;
    }

    return isToggleable ? isOpen : true;
  };

  return (
    <Stack spacing={0}>
      <Inline>
        <button
          aria-expanded={getAriaExpandedValue()}
          aria-label={ariaLabel}
          className={cx(resetButtonStyle, buttonStyles, `header ${isToggleable ? clickable : ''}`)}
          data-testid={`resultPage-${name}-header`}
          type="button"
          {...(!isToggleable && { role: 'heading' })}
          {...(isToggleable && { onClick })}
        >
          <Inline alignY="center" className={inlineStyles}>
            <div aria-hidden className={headerStyles}>
              {label || (
                <>
                  <span>{originName}</span>
                  <span className={separatorStyles}>–</span>
                  <span>{destinationName}</span>
                </>
              )}
            </div>
            {isToggleable && (
              <div aria-hidden className={cx(controlsStyles, 'controls')}>
                <CaretDown aria-hidden className={iconStyles(isOpen)} size={22} weight="light" />
              </div>
            )}
          </Inline>
        </button>
      </Inline>
      <ResetButton
        dataTestId={`resultPage-filterHeader-${name}FilterResetButton-button`}
        onClick={handleReset}
      />
    </Stack>
  );
};

export default FilterHeader;
